import { HASURA_CLAIMS_NAMESPACE } from '@shared';
import type { AuthClaims } from '@shared';

const STORAGE_KEY = 'calldominator.token';
const REFRESH_MARGIN_MS = 60_000;
const MIN_REFRESH_DELAY_MS = 5_000;

type Listener = (token: string | null) => void;

let current: string | null = localStorage.getItem(STORAGE_KEY);
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let refreshStrategy: (() => Promise<string | null>) | null = null;
let unauthorized = false;
const listeners = new Set<Listener>();

export function markUnauthorized() {
  unauthorized = true;
}

export function clearUnauthorized() {
  unauthorized = false;
}

export function getToken() {
  return current;
}

export function getClaims() {
  if (!current) return null;

  try {
    const payload = current.split('.')[1] ?? '';
    const claims: AuthClaims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));

    return claims;
  } catch {
    return null;
  }
}

export function getUserId() {
  return getClaims()?.[HASURA_CLAIMS_NAMESPACE]['x-hasura-user-id'] ?? null;
}

export function setToken(token: string | null) {
  current = token;

  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }

  if (token === null) {
    localStorage.removeItem(STORAGE_KEY);
  } else {
    localStorage.setItem(STORAGE_KEY, token);
    scheduleRefresh(token);
  }

  for (const listener of listeners) listener(token);
}

export function onTokenChange(listener: Listener) {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

export function setRefreshStrategy(strategy: () => Promise<string | null>) {
  refreshStrategy = strategy;

  if (current) scheduleRefresh(current);
}

export async function refreshToken() {
  if (!refreshStrategy || unauthorized) return null;

  const token = await refreshStrategy().catch(() => null);

  setToken(token);

  return token;
}

function tokenExpiryMs(token: string) {
  try {
    const payload = JSON.parse(atob((token.split('.')[1] ?? '').replace(/-/g, '+').replace(/_/g, '/')));

    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

function scheduleRefresh(token: string) {
  if (!refreshStrategy) return;

  const expiry = tokenExpiryMs(token);

  if (expiry === null) return;

  const lifetime = expiry - Date.now();
  const delay = Math.max(Math.min(lifetime * 0.75, lifetime - REFRESH_MARGIN_MS), MIN_REFRESH_DELAY_MS);

  refreshTimer = setTimeout(() => void refreshToken(), delay);
}
